import React, { useState, useEffect } from "react";
import ReceiptHeader from "./ReceiptHeader";
import ItemsTable from "./ItemsTable";
import AddItemModal from "./AddItemModal";
import UploadBatchModal from "./UploadBatchModal"; // <-- New import for CSV upload
import { useCreateItem } from "../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { FiCheckCircle, FiPlus, FiUpload } from "react-icons/fi";
import { useAtom } from "jotai";
import { clinicAtom } from "../../_helper/constant/constant-atom";

const CreateReceipt = () => {
  const navigate = useNavigate("");
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    vendor_id: null,
    receipt_date: "",
    purchase_order_number: "",
    invoice_no: "",
    chalan_no: "",
    purchase_date: "",
    invoice_date: "",
    chalan_date: "",
    items: [],
  });

  const [totals, setTotals] = useState({
    totalTaxableValue: 0,
    cgst: 0,
    sgst: 0,
    totalIGST: 0,
    total_gst: 0,
    grand_total: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false); // State for CSV upload modal
  const [editingIndex, setEditingIndex] = useState(null);
  const [clinic] = useAtom(clinicAtom);

  // Get pharmacy_id from query string
  const pharmacy_id = searchParams.get("pharmacy_id");

  // Prefill receipt date with today’s date
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFormData((prev) => ({ ...prev, receipt_date: today }));
  }, []);

  console.log("Pharmacy Data", formData);

  // Transform the items into the structure expected by your backend
  function transformData(data) {
    const transformed = data.reduce((result, entry) => {
      const itemName = entry.item.item_name;
      const itemId = entry.item.item_id;

      let item = result.find((itm) => itm.item_name === itemName);
      if (!item) {
        item = {
          items_name: itemName,
          id: itemId,
          clinic_id: clinic.id,
          pharmacy_id: pharmacy_id,
          batch_id: [],
        };
        result.push(item);
      }
      item.batch_id.push({
        batch_no: entry.batchNumber,
        hns_code: entry.hsn,
        price: entry.effectiveMRPPerBase
          ? entry.effectiveMRPPerBase
          : entry.price || 0,
        cgst: entry.cgstRate || 0,
        sgst: entry.sgstRate || 0,
        igst: entry.igstRate || 0,
        total_amount: entry.totalAmount || 0,
        profit_margin: entry.profitMargin || 0,
        profit_per_unit: entry.profitPerUnit || 0,
        total_gst: entry.totalGST || 0,
        received_quantity: entry.effectiveQuantity,
        quantity: entry?.effectiveQuantity,
        batch_price: entry.effectivePricePerBase
          ? entry.effectivePricePerBase
          : entry.pricePerUnit || 0,
        receive_date: new Date(entry.receivedDate).toISOString(),
        expiry_date: new Date(entry.expiryDate).toISOString(),
        location: entry.location || {},
      });
      return result;
    }, []);

    return transformed;
  }

  // Calculate receipt totals whenever items change
  useEffect(() => {
    const calculateTotals = () => {
      const totalTaxableValue = formData.items.reduce(
        (sum, item) => sum + (item.taxableValue || 0),
        0
      );
      const cgst = formData.items.reduce(
        (sum, item) => sum + (item.cgst || 0),
        0
      );
      const sgst = formData.items.reduce(
        (sum, item) => sum + (item.sgst || 0),
        0
      );
      const totalIGST = formData.items.reduce(
        (sum, item) => sum + (item.igst || 0),
        0
      );
      const total_gst = cgst + sgst + totalIGST;
      const grand_total = totalTaxableValue + total_gst;

      setTotals({
        totalTaxableValue,
        cgst,
        sgst,
        totalIGST,
        total_gst,
        grand_total,
      });
    };

    calculateTotals();
  }, [formData.items]);

  const handleFormInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSupplierChange = (supplier) => {
    if (supplier) {
      const newVendor = {
        id: supplier.value,
        vendor_name: supplier.label,
      };
      setFormData({ ...formData, vendor_id: newVendor });
    } else {
      setFormData({ ...formData, vendor_id: null });
    }
  };

  const handleAddItem = (newItem) => {
    if (editingIndex !== null) {
      const updatedItems = [...formData.items];
      updatedItems[editingIndex] = newItem;
      setFormData({ ...formData, items: updatedItems });
    } else {
      setFormData({ ...formData, items: [...formData.items, newItem] });
    }
    setEditingIndex(null);
    setIsModalOpen(false);
  };

  const handleEditItem = (index) => {
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  // Callback for when batch items are uploaded via CSV
  const handleBatchUpload = (uploadedItems) => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, ...uploadedItems],
    }));
    toast.success("Batch items uploaded successfully!");
  };

  const mutation = useCreateItem(ApiEndPoint.PHARMACY);
  const handleSubmit = () => {
    // Validation: Vendor must be selected
    if (!formData.vendor_id) {
      toast.error("Vendor is required.");
      return;
    }
    if (!formData.chalan_date) {
      toast.error("Challan date required.");
      return;
    }
    if (!formData.invoice_date) {
      toast.error("Invoice date required.");
      return;
    }
    if (!formData.purchase_date) {
      toast.error("Purchase date required.");
      return;
    }
    if (!formData.receipt_date) {
      toast.error("Receipt date required.");
      return;
    }
    if (!formData.invoice_no) {
      toast.error("Invoice no. required.");
      return;
    }

    // Validation: At least one item must be added
    if (formData.items.length === 0) {
      toast.error("Please add at least one item to the receipt.");
      return;
    }
    const ItemBatchoutput = transformData(formData.items);

    const payload = {
      ...formData,
      supplier_name: formData.vendor_id.vendor_name,
      cgst: totals.cgst,
      igst: totals.totalIGST,
      sgst: totals.sgst,
      total_gst: totals.total_gst,
      grand_total: totals.grand_total,
      invoice_date: formData.invoice_date || null,
      purchase_date: formData.purchase_date || null,
      purchase_order_number: formData.purchase_order_number || null,
      receipt_date: formData.receipt_date || null,
      chalan_date: formData.chalan_date || null,
      chalan_no: formData.chalan_no || null,
      invoice_no: formData.invoice_no || null,
      pharmacy_id: pharmacy_id || null,

      vendor_id: {
        ...formData.vendor_id,
        items_id: ItemBatchoutput,
      },
    };

    mutation.mutate(
      {
        action: "postInventoryReceipt",
        ...payload,
      },
      {
        onSuccess: () => {
          navigate("/new/pharmacy?type=Inventory&t=view");
          // Reset form data
          setFormData({
            vendor_id: null,
            receipt_date: new Date().toISOString().split("T")[0],
            purchase_order_number: "",
            invoice_no: "",
            chalan_no: "",
            purchase_date: "",
            invoice_date: "",
            chalan_date: "",
            items: [],
          });
          setTotals({
            totalTaxableValue: 0,
            cgst: 0,
            sgst: 0,
            totalIGST: 0,
            total_gst: 0,
            grand_total: 0,
          });
          toast.success("Receipt captured successfully!");
        },
        onError: () => {
          toast.error("Failed to capture receipt.");
        },
      }
    );
  };

  return (
    <div className="bg-gray-100 p-6 rounded-lg shadow-lg">
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-700 hover:text-gray-900 transition"
        >
          <FaArrowLeft className="text-xl" />
        </button>
        <h2 className="text-3xl font-bold text-gray-800 flex items-center">
          Create Inventory Receipt
        </h2>
      </div>

      {/* Receipt header section */}
      <ReceiptHeader
        formData={formData}
        handleFormInputChange={handleFormInputChange}
        handleSupplierChange={handleSupplierChange}
      />

      {/* Items table */}
      <ItemsTable
        items={formData.items}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
      />

      {/* Buttons to add a single item or upload a CSV batch */}
      <div className="flex space-x-4 mt-4">
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center gap-2"
          onClick={() => setIsModalOpen(true)}
        >
          <FiPlus className="text-lg" />
          Add Item
        </button>
        <button
          className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 flex items-center gap-2"
          onClick={() => setIsUploadModalOpen(true)}
        >
          <FiUpload className="text-lg" />
          Upload Batches
        </button>
      </div>

      {/* Totals Section */}
      <div className="mt-6 bg-white rounded-lg shadow-md p-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Receipt Totals
        </h3>
        <div className="grid grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              CGST
            </label>
            <input
              type="text"
              className="border border-gray-300 rounded px-4 py-2 w-full bg-gray-100"
              value={`₹${totals.cgst.toFixed(2)}`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              SGST
            </label>
            <input
              type="text"
              className="border border-gray-300 rounded px-4 py-2 w-full bg-gray-100"
              value={`₹${totals.sgst.toFixed(2)}`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              IGST
            </label>
            <input
              type="text"
              className="border border-gray-300 rounded px-4 py-2 w-full bg-gray-100"
              value={`₹${totals.totalIGST.toFixed(2)}`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Total GST
            </label>
            <input
              type="text"
              className="border border-gray-300 rounded px-4 py-2 w-full bg-gray-100"
              value={`₹${totals.total_gst.toFixed(2)}`}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Grand Total
            </label>
            <input
              type="text"
              className="border border-gray-300 rounded px-4 py-2 w-full bg-gray-100"
              value={`₹${totals.grand_total.toFixed(2)}`}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Submit button */}
      <div className="mt-6 flex space-x-4">
        <button
          className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 flex items-center gap-2"
          onClick={handleSubmit}
        >
          <FiCheckCircle className="text-lg" />
          Submit Receipt
        </button>
      </div>

      {/* Modals */}
      {isModalOpen && (
        <AddItemModal
          onClose={() => setIsModalOpen(false)}
          onAddItem={handleAddItem}
          item={editingIndex !== null ? formData.items[editingIndex] : null}
          pharmacy_id={pharmacy_id}
        />
      )}

      {isUploadModalOpen && (
        <UploadBatchModal
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={handleBatchUpload}
          isPublished={false} // Not needed for this scenario
        />
      )}
    </div>
  );
};

export default CreateReceipt;
